import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "sonner";

// Phase 8a₁ — Connections store (UI cache + demo labels).
// **Configuration status source of truth:** GET /api/connections (see useBackendConnections).
// This store holds disconnected defaults and optional demo labels for surfaces that
// still read account names client-side (inbox, outbound). Never seed as connected.

const EMPTY_CHANNEL = (label) => ({
  status: "disconnected",
  label,
  identity: null,
  device: null,
  pairedAt: null,
  lastSync: null,
  messagesToday: 0,
});

const DEFAULT_STATE = {
  channels: {
    whatsapp: { ...EMPTY_CHANNEL("WhatsApp") },
    linkedin: { ...EMPTY_CHANNEL("LinkedIn"), pool: [] },
    telegram: { ...EMPTY_CHANNEL("Telegram"), botUsername: null },
    gmail: { ...EMPTY_CHANNEL("Gmail"), inboxScope: null },
    calendar: { ...EMPTY_CHANNEL("Google Calendar") },
    ios: { ...EMPTY_CHANNEL("iOS Companion"), pushEnabled: false },
    android: { ...EMPTY_CHANNEL("Android Companion") },
  },
  milestonesSeen: [],
};

// Milestone toast messages — Maya-voice
const MILESTONES = [
  { count: 1, message: "First channel paired — Maya can start working.", emoji: "🎉" },
  { count: 3, message: "Three channels live. Maya is now covering most of your inbound.", emoji: "🎉" },
  { count: 5, message: "Five channels live. Maya is everywhere your customers are.", emoji: "🎉" },
  { count: 7, message: "Full coverage. Maya is now wherever you are.", emoji: "🎉🎉" },
];

function countConnected(channels) {
  return Object.values(channels).filter((c) => c.status === "connected").length;
}

function maybeFireMilestone(get, set) {
  const s = get();
  const count = countConnected(s.channels);
  const milestone = MILESTONES.find((m) => m.count === count);
  if (!milestone) return;
  if (s.milestonesSeen?.includes(count)) return;
  set((st) => ({ milestonesSeen: [...(st.milestonesSeen || []), count] }));
  setTimeout(() => {
    toast.success(milestone.message, {
      description: `${milestone.emoji}  ${count} of ${Object.keys(s.channels).length} channels connected`,
      duration: 4500,
    });
  }, 300);
}

/** Map backend connection rows onto the local channel cache (for demo labels). */
function isBackendConfigured(row) {
  if (!row) return false;
  if (typeof row.configured === "boolean") return row.configured;
  if (row.status !== "connected") return false;
  if (row.kind === "gmail" || row.channel_config?.google_oauth) {
    const g = row.channel_config?.google_oauth || {};
    if (g.dev_mock) return false;
    if ((g.google_email || row.account_identifier || "").endsWith("@local.sellix")) return false;
  }
  return true;
}

function mapBackendRow(kind, row) {
  if (!isBackendConfigured(row)) return { status: "disconnected" };
  const base = { status: "connected", pairedAt: row.updated_at || "Connected" };
  if (kind === "linkedin") {
    const li = row.channel_config?.linkedin || {};
    const name = li.display_name || "LinkedIn account";
    const handle = row.account_identifier || li.profile_url || "";
    return {
      ...base,
      pool: [{
        id: row.id,
        name,
        handle,
        avatar: name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "LI",
        pairedAt: base.pairedAt,
        primary: true,
      }],
    };
  }
  if (kind === "whatsapp" || kind === "gmail") {
    const email = kind === "gmail"
      ? (row.google_email || row.channel_config?.google_oauth?.google_email || row.account_identifier)
      : row.account_identifier;
    return { ...base, identity: email || null };
  }
  return base;
}

export const useConnectionsStore = create(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      /** Hydrate display labels from backend rows without treating localStorage as status truth. */
      syncFromBackend: (rows) => set((s) => {
        const byKind = Object.fromEntries((rows || []).map((r) => [r.kind, r]));
        const next = { ...s.channels };
        for (const kind of Object.keys(next)) {
          const row = byKind[kind];
          const patch = mapBackendRow(kind, row);
          next[kind] = { ...next[kind], ...patch };
          if (!isBackendConfigured(row)) {
            next[kind] = {
              ...DEFAULT_STATE.channels[kind],
              ...next[kind],
              status: "disconnected",
              label: DEFAULT_STATE.channels[kind]?.label || kind,
            };
            if (kind === "linkedin") next[kind].pool = [];
          }
        }
        if (isBackendConfigured(byKind.gmail)) {
          next.calendar = {
            ...next.calendar,
            status: "connected",
            identity: byKind.gmail.google_email || byKind.gmail.account_identifier,
          };
        }
        return { channels: next };
      }),

      patchChannel: (id, patch) => set((s) => ({
        channels: { ...s.channels, [id]: { ...s.channels[id], ...patch } },
      })),

      startPairing: (id) => set((s) => ({ channels: { ...s.channels, [id]: { ...s.channels[id], status: "pairing" } } })),
      cancelPairing: (id) => set((s) => {
        const prev = s.channels[id];
        if (id === "linkedin" && prev.pool && prev.pool.length > 0) {
          return { channels: { ...s.channels, linkedin: { ...prev, status: "connected" } } };
        }
        return { channels: { ...s.channels, [id]: { ...prev, status: "disconnected" } } };
      }),

      completeChannel: (id, patch) => {
        set((s) => ({
          channels: {
            ...s.channels,
            [id]: { ...s.channels[id], ...patch, status: "connected" },
          },
        }));
        maybeFireMilestone(get, set);
      },

      completeWhatsApp: ({ identity, device }) => {
        set((s) => ({
          channels: {
            ...s.channels,
            whatsapp: {
              ...s.channels.whatsapp,
              status: "connected",
              identity,
              device,
              pairedAt: "Connected just now",
              lastSync: "just now",
              messagesToday: 0,
            },
          },
        }));
        maybeFireMilestone(get, set);
      },

      disconnectChannel: (id) => set((s) => {
        if (id === "linkedin") {
          return { channels: { ...s.channels, linkedin: { ...s.channels.linkedin, status: "disconnected", pool: [] } } };
        }
        const cleared = { ...s.channels[id], status: "disconnected" };
        if ("identity" in cleared) cleared.identity = null;
        if ("device" in cleared && id !== "ios" && id !== "android") cleared.device = null;
        if ("botUsername" in cleared) cleared.botUsername = null;
        return { channels: { ...s.channels, [id]: cleared } };
      }),

      addLinkedInAccount: (acct) => {
        set((s) => ({
          channels: {
            ...s.channels,
            linkedin: {
              ...s.channels.linkedin,
              status: "connected",
              pool: [{ ...acct, primary: true }],
            },
          },
        }));
        maybeFireMilestone(get, set);
      },
      removeLinkedInAccount: (acctId) => set((s) => {
        const pool = (s.channels.linkedin.pool || []).filter((a) => a.id !== acctId);
        if (pool.length > 0 && !pool.some((a) => a.primary)) pool[0].primary = true;
        return {
          channels: {
            ...s.channels,
            linkedin: {
              ...s.channels.linkedin,
              pool,
              status: pool.length > 0 ? "connected" : "disconnected",
            },
          },
        };
      }),
      promoteLinkedInPrimary: (acctId) => set((s) => ({
        channels: {
          ...s.channels,
          linkedin: {
            ...s.channels.linkedin,
            pool: (s.channels.linkedin.pool || []).map((a) => ({ ...a, primary: a.id === acctId })),
          },
        },
      })),

      isConnected: (id) => get().channels[id]?.status === "connected",
      connectedCount: () => Object.values(get().channels).filter((c) => c.status === "connected").length,
      totalCount: () => Object.keys(get().channels).length,
      getAccountLabel: (channelId) => {
        const c = get().channels[channelId];
        if (!c) return null;
        if (c.status !== "connected") {
          return { label: c.label, connected: false, identity: null };
        }
        if (channelId === "linkedin") {
          const primary = (c.pool || []).find((a) => a.primary) || (c.pool || [])[0];
          return { label: primary ? `${primary.name}'s LinkedIn` : c.label, connected: true, identity: primary?.handle || null };
        }
        return { label: c.label, connected: true, identity: c.identity || c.device || null };
      },
    }),
    {
      name: "sellix.connections.v1",
      // Do not persist channel status — backend is the source of truth.
      partialize: (state) => ({ milestonesSeen: state.milestonesSeen }),
    },
  ),
);
